/* eslint-disable jsx-a11y/label-has-associated-control, jsx-a11y/no-onchange */
/* The following rule will be deprecated in a future version of eslint-plugin-jsx-a11y.
 * https://github.com/jsx-eslint/eslint-plugin-jsx-a11y/issues/398#issuecomment-729113622
 */
import type { SelectHTMLAttributes } from 'react';
import React from 'react';

import { FieldWrapper } from '../FieldWrapper/FieldWrapper';
import * as styles from './DropDown.styles';
import { getAriaDescribedByForInputField } from 'client-ribbons-design-system/utils/getAriaDescribedByForInputField';

export interface DropDownOption {
  disabled?: boolean;
  optionLabel: string;
  optionValue: string;
}

export type GroupedDropDownOptions<TKey extends string = string> = Record<TKey, DropDownOption[]>;

export interface DropDownProps extends SelectHTMLAttributes<HTMLSelectElement> {
  $completed?: boolean;
  description?: string;
  error?: string | null;
  id: string;
  label: string;
  labelHidden?: boolean;
  options: DropDownOption[] | GroupedDropDownOptions;
}

const DROP_DOWN_CLASSNAME = 'drop-down__input';

const getSelectOptionsElements = (options: DropDownOption[]) =>
  options.map(({ disabled, optionLabel, optionValue }) => (
    <option disabled={disabled} key={`${optionValue}${optionLabel}`} value={optionValue}>
      {optionLabel}
    </option>
  ));

const getSelectOptions = (options: DropDownProps['options']) => {
  if (Array.isArray(options)) {
    return getSelectOptionsElements(options);
  }

  return Object.keys(options).map((optName) => (
    <optgroup key={optName} label={optName}>
      {getSelectOptionsElements(options[optName])}
    </optgroup>
  ));
};

export const DropDown = ({
  $completed = false,
  children,
  className,
  description,
  error,
  id,
  label,
  labelHidden = false,
  options,
  required,
  value,
  ...props
}: DropDownProps) => {
  const fieldId = `${id}-select`;

  return (
    <div className={className}>
      <FieldWrapper
        $completed={$completed}
        description={description}
        error={error}
        fieldId={fieldId}
        label={label}
        labelHidden={labelHidden}
      >
        <select
          aria-describedby={getAriaDescribedByForInputField({
            fieldId,
            description,
            error,
          })}
          aria-invalid={!!error}
          aria-required={required}
          className={DROP_DOWN_CLASSNAME}
          css={styles.select}
          id={fieldId}
          required={required}
          value={value}
          {...props}
        >
          {getSelectOptions(options)}
        </select>
      </FieldWrapper>
    </div>
  );
};
